import {useEffect, useState} from 'react';
import {Box, CircularProgress, Paper, TableContainer} from '@mui/material';
import {TaxHeader, MotorHireTable, MotorVehicleTable, MotorVehicleRow} from '@/tax/sub';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import type {TaxObligationUnderFinanceLeasesVO} from '@/services/auditService/interface/tax-com';

interface TaxSubP21Props {
    annualId: number;
    p121Data?: TaxObligationUnderFinanceLeasesVO | null;
}

type MotorHireRowType = {
    date: string;
    item: string;
    cost: number;
    hpBf: number;
    downPayment: number;
    hpCf: number;
    hpAmount?: number;
    repaymentsForPeriod?: number;
    ia?: number;
};

function mapP121ToView(p121Data: TaxObligationUnderFinanceLeasesVO | null | undefined) {
    const motorHireData: MotorHireRowType[] = (p121Data?.motorVehicleHirePurchase || []).map((row) => ({
        date: row?.date || '',
        item: row?.vehicleName || '',
        cost: Number(row?.cost) || 0,
        hpBf: Number(row?.hpBf) || 0,
        downPayment: Number(row?.downPayment) || 0,
        hpCf: Number(row?.hpCf) || 0,
        hpAmount: row?.hpAmount !== undefined && row?.hpAmount !== null ? Number(row.hpAmount) : undefined,
        repaymentsForPeriod: row?.repaymentsForPeriod !== undefined && row?.repaymentsForPeriod !== null ? Number(row.repaymentsForPeriod) : undefined,
        ia: row?.ia !== undefined && row?.ia !== null ? Number(row.ia) : undefined,
    }));
    const motorDisposalData: MotorVehicleRow[] = (p121Data?.taxMotorVehicles || []).map((row) => ({
        date: row?.date || '',
        item: row?.vehicleName || '',
        atCost: Number(row?.cost) || 0,
        accumulated: Number(row?.accumulated) || 0,
        sales: Number(row?.salesProceed) || 0,
        disposal: (Number(row?.gainOnDisposal) || 0) - (Number(row?.lossOnDisposal) || 0),
    }));
    return {motorHireData, motorDisposalData};
}

export function TaxSubP21({annualId, p121Data: p121DataProp}: TaxSubP21Props) {
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);

    const initialize = useTaxStore((state) => state.initialize);
    const storeP121Data = useTaxStore((state) => state.p121Data);
    const p121Data = p121DataProp !== undefined ? p121DataProp : storeP121Data;

    const [loading, setLoading] = useState(true);
    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);
    const [motorHireData, setMotorHireData] = useState<MotorHireRowType[]>([]);
    const [motorDisposalData, setMotorDisposalData] = useState<MotorVehicleRow[]>([]);

    useEffect(() => {
        const loadHeader = async () => {
            setLoading(true);
            try {
                const info = await getTaxComputationInfo(annualId);
                setTaxInfo(info);

                const companyDetails = await getCompanyDetailsInfo(annualId);
                const companyId = companyDetails?.companyId;

                if (companyId) {
                    await initialize(companyId, annualId);
                } else {
                    console.error('Company ID not found');
                }
            } catch (error) {
                console.error('Failed to load tax computation data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadHeader();
    }, [annualId, getTaxComputationInfo, getCompanyDetailsInfo, initialize]);

    useEffect(() => {
        const mapped = mapP121ToView(p121Data);
        setMotorHireData(mapped.motorHireData);
        setMotorDisposalData(mapped.motorDisposalData);
    }, [p121Data]);

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!taxInfo) {
        return <Box sx={{p: 2}}>Failed to load data</Box>;
    }

    return (
        <Box sx={{p: 3}}>
            <TaxHeader
                companyName={taxInfo.companyName}
                fileNo={taxInfo.taxFileNo || ''}
                yearOfAssessment={taxInfo.yearOfAssessment}
            />

            <TableContainer component={Paper}>
                <MotorHireTable
                    parentCurrency={taxInfo.presentationCurrency}
                    storeRow={motorHireData}
                />

                <MotorVehicleTable
                    tableTitle="Loss on disposal of Motor Vehicles under hire purchase"
                    parentCurrency={taxInfo.presentationCurrency}
                    storeRow={motorDisposalData}
                />
            </TableContainer>
        </Box>
    );
}
